import { createElement, useEffect } from 'react';
import type { ComponentType } from 'react';

import { recordRumMilestone } from './index';
import { RumMilestone } from './types';

/**
 * Reports that a route's page component has committed its first render —
 * `route_page_rendered`, the only initial-load milestone that contains any of
 * the page's own cost, and what `page_ready` resolves to on Hydra-native
 * routes.
 *
 * Every route page has to call this, directly or otherwise, or it drops out of
 * the page_ready series entirely. `lazyRoute` calls it for the 145 pages behind
 * it; a route whose page is a plain static import (`/login/$`, `/logout/$`,
 * `/academy-login`, ...) has no wrapper to do it and must call it itself.
 *
 * An effect rather than a render-time call because the milestone means the
 * render *committed*: for a lazily-loaded page the subtree is not committed
 * while it is suspended, so the effect first runs on the render that actually
 * puts the page on screen. Recording is write-once, so a re-render or
 * StrictMode's double invoke keeps the first timestamp.
 */
export function useRoutePageRendered(): void {
  useEffect(() => {
    recordRumMilestone(RumMilestone.RoutePageRendered);
  }, []);
}

/**
 * Wraps a route component so it reports `route_page_rendered` on commit.
 *
 * For route pages that are plain static imports rather than `lazyRoute`
 * entries — `/login`, `/logout`, `/signup` and ~19 others. Without it those
 * pages render normally but leave the page_ready series entirely, and the
 * remaining p75 is computed over lazyRoute pages only.
 *
 * Deliberately NOT applied to a route whose component redirects
 * (`SafeNavigate`), hosts an embedded app (the child-app milestones own those)
 * or renders the not-found page: none of them is the page the user asked for
 * becoming usable, and marking them ready is the overstatement this milestone
 * exists to remove.
 */
export function withRoutePageRendered<Props extends object>(Component: ComponentType<Props>) {
  function RoutePage(props: Props) {
    useRoutePageRendered();
    return createElement(Component, props);
  }
  RoutePage.displayName = `RoutePage(${Component.displayName ?? Component.name})`;
  return RoutePage;
}
